<template>
  <div class="asset-search-container">
    <div class="search-wrapper" :class="{ 'search-active': isSearchActive }">
      <!-- Search Input -->
      <v-text-field
          v-model="localModel"
          :class="[
          'search-input',
          { 'input-active': isSearchActive }
        ]"
          :placeholder="placeholder || 'Assets suchen...'"
          variant="outlined"
          density="comfortable"
          clearable
          hide-details
          rounded
          @focus="handleFocus"
          @blur="handleBlur"
          @input="handleInput"
          @keydown.arrow-down.prevent="navigateDown"
          @keydown.arrow-up.prevent="navigateUp"
          @keydown.enter.prevent="selectItem"
          @keydown.escape="closeSuggestions"
      >
        <template #prepend-inner>
          <v-icon color="grey-darken-1" @click="props.onSubmit()">mdi-magnify</v-icon>
        </template>

        <template #append-inner>
          <v-btn
              icon
              size="small"
              variant="text"
              class="filter-button bg-secondary"
              @mousedown="handleClick"
              @click="handleClick"
          >
            <v-icon>mdi-tune</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <!-- Dropdown List -->
      <div
          v-if="showSuggestions && filteredAssets.length > 0"
          class="suggestions-dropdown"
          @scroll="handleScroll"
      >
        <v-list density="compact" class="suggestions-list">
          <v-list-item
              v-for="(asset, index) in filteredAssets"
              :key="asset.id"
              :class="{ 'item-highlighted': selectedIndex === index }"
              @click="selectAsset(asset)"
              @mouseenter="selectedIndex = index"
          >
            <template #prepend>
              <v-avatar size="32" :class="asset.is_safety ? 'bg-safetyBtnColor' : 'asset-avatar'">
                <v-icon>{{ asset.prependIcon ?? 'mdi-cube-outline' }}</v-icon>
              </v-avatar>
            </template>

            <div class="d-flex flex-column justify-center flex-grow-1">
              <!-- Title mit Highlighting -->
              <v-list-item-title class="asset-title d-flex align-center gap-1">
                <span v-html="highlightMatch(asset.title, localModel)"></span>
                <v-chip
                    v-if="asset.matchedBy === 'id'"
                    size="x-small"
                    color="primary"
                    variant="tonal"
                    class="ml-1"
                >
                  ID
                </v-chip>
              </v-list-item-title>

              <!-- Subtitle mit Highlighting -->
              <v-list-item-subtitle class="asset-subtitle d-flex align-center gap-1">
                <span v-html="highlightMatch(asset.subtitle, localModel)"></span>
                <v-chip
                    v-if="asset.matchedBy === 'title'"
                    size="x-small"
                    color="secondary"
                    variant="tonal"
                    class="ml-1"
                >
                  Name
                </v-chip>
              </v-list-item-subtitle>
            </div>

            <template #append>
              <v-chip v-if="asset.chip" :color="asset.chip_color" size="x-small" variant="flat">
                {{ asset.chip }}
              </v-chip>
            </template>
          </v-list-item>

        </v-list>
      </div>

      <!-- No Results -->
      <div
          v-if="showSuggestions && filteredAssets.length === 0 && localModel"
          class="no-results"
      >
        <v-list density="compact">
          <v-list-item>
            <template #prepend>
              <v-icon color="grey">mdi-magnify-close</v-icon>
            </template>
            <v-list-item-title class="text-grey">
              Keine Ergebnisse für "{{ localModel }}"
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, nextTick, onMounted, ref, watch} from 'vue'
import {AssetFetcher, AssetItem} from "@/types/Asset";

const model = defineModel();

/*
* model["id"] = asset["id"]
* */

const localModel = ref<string>("");

const props = defineProps<{
  fetchItems?: AssetFetcher,
  assetKeys: string | string[],
  items?: any[],
  onSubmit: () => void,
  onSetting: () => void,
  placeholder?: string,
}>();

const internalItems = ref<AssetItem[]>([])  // Geladene Items
const totalItems = ref(0)                    // Server-Total
const isLoadingAssets = ref(false)
const currentOffset = ref(0)
const PAGE_SIZE = 30
const searchDebounceTimer = ref<number | null>(null)

const isSearchActive = ref(false)
const showSuggestions = ref(false)
const selectedIndex = ref(-1)
const lastIndex = ref(10);
const infiniteScrollKey = ref(0); // Key für Force-Re-render

// Computed properties
/*const filteredAssets = computed(() => {
  if (model.value == null) return props.items.slice(0, lastIndex.value);

  return props.items.filter(asset =>
      asset.title.toLowerCase().includes(model.value.toLowerCase()) ||
      asset.subtitle.toLowerCase().includes(model.value.toLowerCase())
  ).slice(0, lastIndex.value);
})*/

const filteredAssets = computed(() => {
  if (props.fetchItems) {
    return internalItems.value  // Server hat schon gefiltert
  }
  return []
  // Alter Modus: lokale Filterung (backward-compat)
  /*  if (localModel.value == null) return (props.items ?? []).slice(0, lastIndex.value)
    return (props.items ?? []).filter(asset =>
        asset.title.toLowerCase().includes(localModel.value.toLowerCase()) ||
        asset.subtitle.toLowerCase().includes(localModel.value.toLowerCase())
    ).slice(0, lastIndex.value)*/
})

const hasMoreItems = computed(() => currentOffset.value < totalItems.value)

// Watch für showSuggestions um infinite-scroll zurückzusetzen
watch(showSuggestions, (newVal) => {
  if (newVal) {
    // Reset lastIndex when opening suggestions
    lastIndex.value = 10;
    // Force re-render of infinite scroll
    infiniteScrollKey.value++;
  }
});

onMounted(() => {
  if (props.fetchItems) {
    loadPage(0, '')  // Erste Seite im Hintergrund laden
  }
})

async function loadPage(offset: number, search: string) {
  if (isLoadingAssets.value) return
  isLoadingAssets.value = true
  try {
    const result = await props.fetchItems!({offset, limit: PAGE_SIZE, search})
    if (offset === 0) {
      internalItems.value = result.items       // Reset bei neuer Suche
    } else {
      internalItems.value.push(...result.items) // Append beim Scroll
    }
    totalItems.value = result.total
    currentOffset.value = offset + result.items.length
  } catch (e) {
    console.error('Asset fetch failed:', e)
  } finally {
    isLoadingAssets.value = false
  }
}

// Methods
const handleFocus = () => {
  isSearchActive.value = true
  showSuggestions.value = true
  selectedIndex.value = -1
}

const handleBlur = () => {
  // Delay to allow for click events on suggestions
  setTimeout(() => {
    isSearchActive.value = false
    showSuggestions.value = false
    selectedIndex.value = -1
  }, 150)
}

/*const handleInput = () => {
  selectedIndex.value = -1
  lastIndex.value = 10; // Reset beim Tippen
  infiniteScrollKey.value++; // Force re-render
  if (!showSuggestions.value) {
    showSuggestions.value = true
  }
}*/

function handleInput() {
  if (!props.fetchItems) {
    // Alter Modus: lokale Filterung
    selectedIndex.value = -1
    lastIndex.value = 10
    return
  }
  // Neuer Modus: Debounced Server-Suche
  if (searchDebounceTimer.value) clearTimeout(searchDebounceTimer.value)
  searchDebounceTimer.value = window.setTimeout(() => {
    currentOffset.value = 0
    loadPage(0, localModel.value || '')
  }, 300)
}

const handleClick = (event: any) => {
  event.preventDefault();
  event.stopPropagation();
  props.onSetting();
}

const navigateDown = () => {
  if (selectedIndex.value < filteredAssets.value.length - 1) {
    selectedIndex.value++
    scrollToSelected()
  }
}

const navigateUp = () => {
  if (selectedIndex.value > 0) {
    selectedIndex.value--
    scrollToSelected()
  } else {
    selectedIndex.value = -1
  }
}

const scrollToSelected = () => {
  nextTick(() => {
    const dropdown = document.querySelector('.suggestions-dropdown')
    const highlighted = dropdown?.querySelector('.item-highlighted')
    if (highlighted) {
      highlighted.scrollIntoView({ block: 'nearest' })
    }
  })
}

const selectItem = () => {
  if (selectedIndex.value >= 0 && selectedIndex.value < filteredAssets.value.length) {
    selectAsset(filteredAssets.value[selectedIndex.value])
    props.onSubmit();
  }
}

const selectAsset = (asset: any) => {
  localModel.value = asset.title
  for (const key of [props.assetKeys].flat()) {
    const val = asset[key]
    if (val != null) model.value[key] = `=${val}`

  }
  showSuggestions.value = false
  isSearchActive.value = false
  selectedIndex.value = -1
  props.onSubmit();
}

const closeSuggestions = () => {
  showSuggestions.value = false
  isSearchActive.value = false
  selectedIndex.value = -1
}

const handleScroll = (event: Event) => {
  const element = event.target as HTMLElement;
  const scrollTop = element.scrollTop;
  const scrollHeight = element.scrollHeight;
  const clientHeight = element.clientHeight;

  const threshold = 50;
  const isNearBottom = scrollTop + clientHeight >= scrollHeight - threshold;

  /*  if (isNearBottom) {
      lastIndex.value += 10;
    }*/

  if (isNearBottom && hasMoreItems.value && !isLoadingAssets.value) {
    loadPage(currentOffset.value, localModel.value || '')
  }
}

function highlightMatch(text: string, search: string | null): string {
  if (!search || !text) return text
  text = String(text)
  const regex = new RegExp(`(${escapeRegex(search)})`, 'gi')
  return text.replace(regex, '<mark class="search-highlight">$1</mark>')
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}
</script>

<style scoped>
.asset-search-container {
  position: relative;
  margin: 0 auto;
}

.search-wrapper {
  position: relative;
}

.search-input {
  transition: all 0.2s ease;
}

.search-input :deep(.v-field) {
  border-radius: 28px !important;
  transition: border-radius 0.2s ease;
}

.search-input.input-active :deep(.v-field) {
  border-bottom-left-radius: 4px !important;
  border-bottom-right-radius: 4px !important;
}

.search-input :deep(.v-field__outline) {
  --v-field-border-opacity: 0.12;
}

.search-input :deep(.v-field--focused .v-field__outline) {
  --v-field-border-opacity: 1;
}

.filter-button {
  margin-right: -4px;
}

.suggestions-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: rgb(var(--v-theme-surface));
  border: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-top: none;
  border-radius: 0 0 12px 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
}

.no-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: rgb(var(--v-theme-surface));
  border: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-top: none;
  border-radius: 0 0 12px 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
}

.suggestions-list {
  padding: 0;
}

.suggestions-list :deep(.v-list-item) {
  padding: 12px 16px;
  min-height: 64px;
  border-bottom: thin solid rgba(var(--v-border-color), 0.06);
  transition: background-color 0.2s ease;
}

.suggestions-list :deep(.v-list-item:last-child) {
  border-bottom: none;
}

.suggestions-list :deep(.v-list-item:hover),
.suggestions-list :deep(.v-list-item.item-highlighted) {
  background-color: rgba(var(--v-theme-primary), 0.25);
}

.asset-avatar {
  background-color: rgba(var(--v-theme-primary), 0.1);
  color: rgb(var(--v-theme-primary));
}

.asset-title {
  font-weight: 500;
  line-height: 1.2;
}

.asset-subtitle {
  font-size: 0.8125rem;
  opacity: 0.7;
}

.selected-asset {
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Scrollbar styling for suggestions dropdown */
.suggestions-dropdown::-webkit-scrollbar {
  width: 6px;
}

.suggestions-dropdown::-webkit-scrollbar-track {
  background: transparent;
}

.suggestions-dropdown::-webkit-scrollbar-thumb {
  background: rgba(var(--v-theme-on-surface), 0.2);
  border-radius: 3px;
}

.suggestions-dropdown::-webkit-scrollbar-thumb:hover {
  background: rgba(var(--v-theme-on-surface), 0.3);
}

.chip-container {
  width: 80px; /* Feste Breite für den Chip-Bereich */
  display: flex;
  justify-content: center;
  align-items: center;
}

.search-highlight {
  background-color: rgba(var(--v-theme-primary), 0.3);
  padding: 0 2px;
  border-radius: 2px;
  font-weight: 600;
}
</style>